import React, { FC, useState } from "react"
import { ArtistTrendEconomy, RoyaltyChartData } from "../../../../types"
import { KSpan, KTitleSpan } from "kahuna-base-react-components"
import NotFound from "../../../NotFound"
import { lang } from "../../../../constants/languages"
import { formatNumber, replaceCurrencies } from "../../../../utility"
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler
} from "chart.js"
import { Line } from "react-chartjs-2"

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Filler)

type RoyaltyThreeMonthsProps = {
  users: ArtistTrendEconomy[]
  chartData: RoyaltyChartData[]
  totalRoyalty: number
}

const RoyaltyThreeMonths: FC<RoyaltyThreeMonthsProps> = (props) => {
  const { users, chartData, totalRoyalty } = props

  const [hoverId, setHoverId] = useState<number>()

  const getLast3MonthsLabels = () => {
    const labels: string[] = []
    const today = new Date()
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

    for (let i = 2; i >= 0; i--) {
      // oldest first
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1)
      const month = monthNames[date.getMonth()]
      const year = date.getFullYear()
      labels.push(`${month} ${year}`)
    }

    return labels
  }
  const last30 = users.reduce((s, u) => s + u.totalAmount30, 0)
  const last60 = users.reduce((s, u) => s + u.totalAmount60, 0)
  const last90 = users.reduce((s, u) => s + u.totalAmount90, 0)

  const dynamicLabels = getLast3MonthsLabels() // e.g., ["202510", "202511", "202512"]

  // Original chart labels and data
  const originalLabels = chartData.map((item) => item.monthLabel)
  const originalData = chartData.map((item) => item.royalty)

  // Ensure labels cover last 3 months
  const allLabels = [...new Set([...originalLabels, ...dynamicLabels])]

  const mappedOriginalData = allLabels.map((label) => {
    const index = originalLabels.indexOf(label)
    return index !== -1 ? originalData[index] : 0 // fill 0 if new month
  })

  const totalLineData = allLabels.map((label) => {
    if (label === dynamicLabels[2]) return last30
    if (label === dynamicLabels[1]) return last60
    if (label === dynamicLabels[0]) return last90
    return 0 // or leave 0 for older months
  })

  const lineChartData = {
    labels: allLabels,
    datasets: [
      {
        label: "USD", // existing line
        data: mappedOriginalData,
        borderColor: "#56E099",
        backgroundColor: "rgba(86, 224, 153, 0.1)",
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointBackgroundColor: "#56E099"
      },
      {
        label: "Total Amount (Last 3 Months)", // new line
        data: totalLineData,
        borderColor: "#b5f2ffff",
        backgroundColor: "rgba(133, 221, 247, 0.1)",
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointBackgroundColor: "#b5f2ffff"
      }
    ]
  }

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: "index" as const,
        intersect: false,
        callbacks: {
          label: (context: any) => `USD: ${formatNumber(context.raw.toFixed(2))}`
        }
      },
      datalabels: { display: false }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#666" }
      },
      y: {
        grid: { color: "#f0f0f0" },
        ticks: {
          color: "#666",
          callback: (value: any) => formatNumber(value)
        },
        beginAtZero: true
      }
    }
  }

  return (
    <div className="w-full h-full flex flex-col gap-4">
      {/* Chart Section */}
      <div
        className="py-5 px-7"
        style={{
          borderRadius: "10px",
          border: "1px solid var(--stroke-soft-200, #F3F3F3)",
          background: "var(--bg-white-0, #FFF)",
          boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
        }}
      >
        <div className="flex items-center gap-4 mb-4 justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#56E099" }}></div>
            <KSpan text="Royalty Reports:" fontWeight={600} />
            <KSpan text={formatNumber(totalRoyalty.toFixed(2))} fontWeight={600} fontSize={16} />
          </div>

          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#45cfb8ff" }}></div>
            <KSpan text="Total:" fontWeight={600} />
            <KSpan text={formatNumber((last30 + last60 + totalRoyalty).toFixed(2))} fontWeight={600} fontSize={16} />
          </div>

          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#b5f2ffff" }}></div>
            <KSpan text="Last 90 Days" fontWeight={600} />
            <KSpan text="(Daily Streams * Unit Price)" fontSize={12} fontWeight={500} />
            <KSpan text=":" fontWeight={500} />
            <KSpan text={formatNumber(last30 + last60 + last90)} fontWeight={600} fontSize={16} />
          </div>
        </div>
        <div style={{ height: 200 }}>
          {chartData.length > 0 ? (
            <Line data={lineChartData} options={lineChartOptions} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <KSpan text="No chart data available" color="#999" />
            </div>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div
        className="py-5 px-7 min-h-[100%]"
        style={{
          borderRadius: "10px",
          border: "1px solid var(--stroke-soft-200, #F3F3F3)",
          background: "var(--bg-white-0, #FFF)",
          boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
        }}
      >
        <div>
          <div className="flex" style={{ width: "100%" }}>
            <div style={{ width: "22%" }}></div>
            <div className="flex justify-center" style={{ width: "39%" }}>
              <KSpan color="#111" text={"Gross / Net / Kahuna Share"} />
            </div>
          </div>
          <div className="flex mb-4" style={{ width: "100%" }}>
            <div style={{ width: "26%" }}>
              <KSpan color="#111" text={"User"} />
            </div>
            <div style={{ width: "13%" }}>
              <KSpan color="#111" text={"60-90 days"} />
            </div>
            <div style={{ width: "13%" }}>
              <KSpan color="#111" text={"30-60 days"} />
            </div>
            <div style={{ width: "13%" }}>
              <KSpan color="#111" text={"0-30 days"} />
            </div>
            <div style={{ width: "12%" }}>
              <KSpan color="#111" text={"Sp Unit Price"} />
            </div>
            <div style={{ width: "12%" }}>
              <KSpan color="#111" text={"Gross Unit Price"} />
            </div>
            <div style={{ width: "11%" }}>
              <KSpan color="#111" text={"Net Unit Price"} />
            </div>
          </div>
          {users && !(users.length > 0) && (
            <div className="flex p-5 w-full justify-center mt-[100px]">
              <NotFound title={lang.not_found.trends.title} description={lang.not_found.trends.desc} imageWidth={400} />
            </div>
          )}
          <div className="flex p-2 mb-3" style={{ width: "100%", background: "#00000008", borderRadius: "5px" }}>
            <div style={{ width: "26%" }}>
              <KSpan fontWeight={600} color="#111" text={"TOTAL"} />
            </div>
            <div style={{ width: "13%" }}>
              <KSpan
                fontWeight={600}
                color="#111"
                text={`${formatNumber(
                  (users?.reduce((sum, item) => sum + (item.totalAmount90 || 0), 0) || 0).toFixed(1)
                )}$ / ${formatNumber(
                  (users?.reduce((sum, item) => sum + (item.totalNet90 || 0), 0) || 0).toFixed(1)
                )}$`}
              />
              <div className="flex ml-4">
                <KSpan
                  fontWeight={600}
                  color="green"
                  text={`+${formatNumber(
                    (
                      (users?.reduce((sum, item) => sum + (item.totalAmount90 || 0), 0) || 0) -
                      (users?.reduce((sum, item) => sum + (item.totalNet90 || 0), 0) || 0)
                    ).toFixed(1)
                  )}$`}
                />
              </div>
            </div>
            <div style={{ width: "13%" }}>
              <KSpan
                fontWeight={600}
                color="#111"
                text={`${formatNumber(
                  (users?.reduce((sum, item) => sum + (item.totalAmount60 || 0), 0) || 0).toFixed(1)
                )}$ / ${formatNumber(
                  (users?.reduce((sum, item) => sum + (item.totalNet60 || 0), 0) || 0).toFixed(1)
                )}$`}
              />
              <div className="flex ml-4">
                <KSpan
                  fontWeight={600}
                  color="green"
                  text={`+${formatNumber(
                    (
                      (users?.reduce((sum, item) => sum + (item.totalAmount60 || 0), 0) || 0) -
                      (users?.reduce((sum, item) => sum + (item.totalNet60 || 0), 0) || 0)
                    ).toFixed(1)
                  )}$`}
                />
              </div>
            </div>
            <div style={{ width: "13%" }}>
              <KSpan
                fontWeight={600}
                color="#111"
                text={`${formatNumber(
                  (users?.reduce((sum, item) => sum + (item.totalAmount30 || 0), 0) || 0).toFixed(1)
                )}$ / ${formatNumber(
                  (users?.reduce((sum, item) => sum + (item.totalNet30 || 0), 0) || 0).toFixed(1)
                )}$`}
              />
              <div className="flex ml-4">
                <KSpan
                  fontWeight={600}
                  color="green"
                  text={`+${formatNumber(
                    (
                      (users?.reduce((sum, item) => sum + (item.totalAmount30 || 0), 0) || 0) -
                      (users?.reduce((sum, item) => sum + (item.totalNet30 || 0), 0) || 0)
                    ).toFixed(1)
                  )}$`}
                />
              </div>
            </div>
          </div>
          {users?.map((artistTrendEconomy) => {
            const background = hoverId === artistTrendEconomy.userId ? "#00000008" : "transparent"
            return (
              <div
                className="flex p-2"
                style={{ width: "100%", background, borderRadius: "5px" }}
                key={`trend_top_track_${artistTrendEconomy.userId}}`}
                onMouseOver={() => setHoverId(artistTrendEconomy.userId)}
              >
                <div style={{ width: "26%" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px"
                    }}
                  >
                    <img
                      src={artistTrendEconomy?.user?.photo || "/account_icons/avatar.svg"}
                      alt={artistTrendEconomy?.user?.firstName}
                      style={{ width: "50px", height: "50px", borderRadius: "50%" }}
                    />
                    <div>
                      <div>
                        <KTitleSpan
                          fontSize={16}
                          lineHeight="20px"
                          text={`${artistTrendEconomy?.user?.firstName} ${artistTrendEconomy?.user?.lastName}`}
                        />
                      </div>
                      <div>
                        <KSpan fontSize={12} text={artistTrendEconomy?.user?.email} />
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ width: "13%" }}>
                  <KSpan
                    text={`${formatNumber((artistTrendEconomy.totalAmount90 || 0).toFixed(1))}$ / ${formatNumber(
                      (artistTrendEconomy.totalNet90 || 0).toFixed(1)
                    )}$`}
                  />
                  <div className="flex ml-4">
                    <KSpan
                      fontWeight={600}
                      color="green"
                      text={`+${formatNumber(
                        (artistTrendEconomy.totalAmount90 - artistTrendEconomy.totalNet90).toFixed(1)
                      )}$`}
                    />
                  </div>
                </div>
                <div style={{ width: "13%" }}>
                  <KSpan
                    text={`${formatNumber((artistTrendEconomy.totalAmount60 || 0).toFixed(1))}$ / ${formatNumber(
                      (artistTrendEconomy.totalNet60 || 0).toFixed(1)
                    )}$`}
                  />
                  <div className="flex ml-4">
                    <KSpan
                      fontWeight={600}
                      color="green"
                      text={`+${formatNumber(
                        (artistTrendEconomy.totalAmount60 - artistTrendEconomy.totalNet60).toFixed(1)
                      )}$`}
                    />
                  </div>
                </div>
                <div style={{ width: "13%" }}>
                  <KSpan
                    text={`${formatNumber((artistTrendEconomy.totalAmount30 || 0).toFixed(1))}$ / ${formatNumber(
                      (artistTrendEconomy.totalNet30 || 0).toFixed(1)
                    )}$`}
                  />
                  <div className="flex ml-4">
                    <KSpan
                      fontWeight={600}
                      color="green"
                      text={`+${formatNumber(
                        (artistTrendEconomy.totalAmount30 - artistTrendEconomy.totalNet30).toFixed(1)
                      )}$`}
                    />
                  </div>
                </div>
                <div style={{ width: "12%" }}>
                  <KSpan
                    color="#111"
                    text={`${artistTrendEconomy.spUnitPrice?.toFixed(5)} ${replaceCurrencies(
                      artistTrendEconomy.currency
                    )}`}
                  />
                </div>
                <div style={{ width: "12%" }}>
                  <KSpan
                    color="#111"
                    text={`${artistTrendEconomy.grossUnitPrice?.toFixed(5)} ${replaceCurrencies(
                      artistTrendEconomy.currency
                    )}`}
                  />
                </div>
                <div style={{ width: "11%" }}>
                  <KSpan
                    color="#111"
                    text={`${artistTrendEconomy.netUnitPrice?.toFixed(5)} ${replaceCurrencies(
                      artistTrendEconomy.currency
                    )}`}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default RoyaltyThreeMonths
